/**
 * HandEnd (DESIGN-client §7): the scoreboard from the last `handScored` cue, plus "Next hand in Ns"
 * from the server hold timer.
 *
 * This screen is mounted by `shell/routing.ts` when that cue *ends*, not when the phase flips, so
 * by the time it renders the count-up has already walked to the totals it is about to show. It
 * therefore reads the cue's own numbers and does not try to follow the walk.
 *
 * It used to be a five-row table of grey text with the totals in the footer, which is what the
 * playtest meant by "boring": the two things a player wants off this screen — did we make it, and
 * what is the score now — were the two smallest things on it. So the hand is now told as two
 * TEAM COLUMNS in the teams' own colours, with the running total counting up from where it started
 * the hand to where it finished, the delta glowing on whichever side gained, and SET or BOSTON
 * stamping in over the column it happened to. The rows are still there, under the number, because
 * the arithmetic is how a player checks the number.
 */
import { useEffect, useRef, useState } from 'react';
import { scoreLines, type TeamHandScore } from '../../scene/table/scoreLines';
import { useStore } from '../../store/store';
import { teamColorVar, teamLabel, teamOfSeat } from '../helpers';
import { Panel } from '../ui';

const HOLD_FALLBACK_MS = 6000;
/** How long a total takes to walk from its pre-hand value to its new one. */
export const COUNT_MS = 1200;

/**
 * Reduced motion, from the OS or from Settings — the same pair `theme.css` folds together for CSS.
 *
 * The count-up is JavaScript, so the stylesheet's blanket `animation-duration: 0.001ms` cannot
 * reach it. A player who asked for stillness gets the final totals immediately instead.
 */
function useReducedMotion(): boolean {
  const pref = useStore((s) => s.settings.motion);
  const [os, setOs] = useState(() => {
    try {
      return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    try {
      const mq = matchMedia('(prefers-reduced-motion: reduce)');
      const on = (e: MediaQueryListEvent): void => setOs(e.matches);
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    } catch {
      return;
    }
  }, []);
  if (pref === 'reduced') return true;
  if (pref === 'full') return false;
  return os;
}

/**
 * Walk `from` → `to` over `COUNT_MS`, on rAF, easing out.
 *
 * Rounded every frame and pinned to `to` on the last one, because this number carries a
 * `data-testid` that four specs assert an exact string against.
 */
function useCountUp(from: number, to: number, run: boolean): number {
  const [value, setValue] = useState(run ? from : to);
  const frame = useRef(0);
  useEffect(() => {
    if (!run) {
      setValue(to);
      return;
    }
    const started = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - started) / COUNT_MS);
      // Cubic ease-out: fast off the mark, so the size of the swing reads before the number lands.
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(t >= 1 ? to : Math.round(from + (to - from) * eased));
      if (t < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [from, to, run]);
  return value;
}

function Delta({ n }: { n: number }) {
  return <span className={n < 0 ? 'delta-neg' : n > 0 ? 'delta-pos' : ''}>{n > 0 ? `+${n}` : n}</span>;
}

interface LineProps {
  label: string;
  value: string;
  i: number;
  /** Points, when the row's value is a signed number that should be read as one. */
  points?: number;
}

function Line({ label, value, i, points }: LineProps) {
  const tone = points === undefined || points === 0 ? '' : points < 0 ? ' delta-neg' : ' delta-pos';
  return (
    <div className="handend__line" style={{ '--i': i } as React.CSSProperties}>
      <span className="handend__label">{label}</span>
      <span className={`handend__value tnum${tone}`}>{value}</span>
    </div>
  );
}

const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

/**
 * The two emphases on this screen, and the difference between them.
 *
 * A player in seat 1 or 3 is on team 1, and the blue column kept the ring round it whatever they
 * did — reported from a real game as "when I play as red, the blue team still has the highlight".
 * The lit column is **mine**, always, because that is what the ring is for: finding my own score on
 * a screen whose columns are in the wire's team order and so do not move between hands.
 *
 * The delta glow is a different question with a different answer — which side actually gained this
 * hand — and it stays keyed to the numbers. A hand both teams lost points on, or that moved
 * neither, belongs to nobody and nothing glows.
 *
 * Pure and exported so the seat arithmetic can be asserted without a renderer; a spectator has no
 * seat and gets no ring.
 */
export function handEndEmphasis(
  seat: number | null,
  deltas: readonly [number, number] | null,
): { mine: 0 | 1 | null; gained: 0 | 1 | null } {
  const mine = seat == null ? null : teamOfSeat(seat);
  const gained = !deltas ? null : deltas[0] > deltas[1] ? 0 : deltas[1] > deltas[0] ? 1 : null;
  return { mine, gained };
}

/**
 * The hand's rows for one team: what it took, what each part of that was worth, and where the bags
 * ended up.
 *
 * Two things this fixes, both from a read of the first pass. `Contract 6/5` was read as a fraction
 * rather than as "six taken against a bid of five", so the two numbers are named. And `Books: Made`
 * was a status with no number next to it, printed directly under a SET stamp that had already said
 * the same word — so the status row is gone and what is left is the PRICE: `Set −50`, `Nil made
 * +100`, `Boston +200`.
 *
 * The prices come from `scene/table/scoreLines`, which is the same breakdown the count-up walks on
 * the felt, so the screen and the plaques cannot disagree about what a hand was worth. It is a pure
 * module with one type import and no Pixi in it; duplicating its reconciliation here — it prices
 * what it can at the standard rule and puts the remainder on the contract line, so the parts always
 * sum to the server's authoritative `delta` — is the worse of the two couplings.
 */
function handRows(team: TeamHandScore): LineProps[] {
  const priced = scoreLines(team);
  const at = (kind: 'contract' | 'penalty' | 'boston'): number | undefined =>
    priced.find((l) => l.kind === kind)?.delta;
  const nils = priced.filter((l) => l.kind === 'nil');
  const rows: LineProps[] = [];

  rows.push({
    label: 'Books',
    value: `${team.taken} · Bid ${team.contract}${team.boardApplied ? ' · board' : ''}`,
    i: rows.length,
  });

  const contract = at('contract');
  if (contract !== undefined) {
    rows.push({ label: team.made ? 'Made' : 'Set', value: signed(contract), i: rows.length, points: contract });
  }

  for (const nil of nils) {
    rows.push({ label: nil.label, value: signed(nil.delta), i: rows.length, points: nil.delta });
  }

  // Where the pile started and where it ended. The starting count is `bagsAfter - bagsAdded` only
  // while nothing reset it: a penalty subtracts the rule's threshold, possibly more than once, and
  // the cue carries neither the threshold nor how many times it fired — so a team that went 7 → 10
  // → 0 would be shown starting at −3. Under a penalty the row says what was GAINED instead, and
  // the penalty row underneath says where the rest went.
  rows.push({
    label: 'Bags',
    value: team.bagPenalty
      ? `+${team.bagsAdded} → ${team.bagsAfter}`
      : `${team.bagsAfter - team.bagsAdded} → ${team.bagsAfter}`,
    i: rows.length,
  });

  const penalty = at('penalty');
  if (penalty !== undefined) {
    rows.push({ label: 'Bag penalty', value: signed(penalty), i: rows.length, points: penalty });
  }

  const boston = at('boston');
  if (boston !== undefined) {
    rows.push({ label: 'Boston', value: signed(boston), i: rows.length, points: boston });
  }
  return rows;
}

export function HandEnd() {
  const result = useStore((s) => s.presentation.lastHandResult);
  const table = useStore((s) => s.table);
  const seat = useStore((s) => s.session.seat);
  const displayed = useStore((s) => s.presentation.displayedScores);
  const reduced = useReducedMotion();
  const [now, setNow] = useState(Date.now());
  const [mountedAt] = useState(Date.now());

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);

  const endsAt = table?.timer.kind === 'scored' || table?.timer.kind === 'hold' ? table.timer.endsAt : mountedAt + HOLD_FALLBACK_MS;
  const secs = Math.max(0, Math.ceil((endsAt - now) / 1000));
  // The cue's own totals, not `displayedScores`.
  //
  // This screen used to prefer `displayedScores` while the queue was busy, on the assumption that
  // it was showing a count-up in progress. It is not: the panel now only appears once the
  // `handScored` cue has ended (`shell/routing.ts`), so the walk is already over and
  // `displayedScores` holds whatever it had reached — which, when the panel arrived early, was the
  // score from *before* the hand. `displayedScores` stays as the fallback for the backstop path,
  // where there was no cue and nothing walked anywhere.
  const totals: [number, number] = result?.totals ?? displayed;
  const teams = result?.teams;

  // The pre-hand total is not on the cue; it is the total minus what the hand paid. Counting from
  // there is the whole point — a number that starts at its own value has nothing to say.
  const before: [number, number] = [totals[0] - (teams?.[0].delta ?? 0), totals[1] - (teams?.[1].delta ?? 0)];
  const count0 = useCountUp(before[0], totals[0], !!teams && !reduced);
  const count1 = useCountUp(before[1], totals[1], !!teams && !reduced);
  const shown: [number, number] = [count0, count1];
  const { mine, gained } = handEndEmphasis(seat, teams ? [teams[0].delta, teams[1].delta] : null);

  return (
    <div className="screen screen--center screen--overlay" data-testid="screen-hand-end">
      <div className="screen__inner">
        <div className="row row--between">
          <h1 className="display display--lg">Hand {result?.handNo ?? table?.handNo ?? ''}</h1>
          <span className="muted tnum" data-testid="next-hand-in">
            Next hand in {secs}s
          </span>
        </div>
        {/* Routing on the cue means the numbers are normally here before this screen is, so this
            branch is now only for the backstop path: a driver with no cue at all, where the panel
            appears on a timer with nothing to fill it. A grid of em-dashes and a 0-0 total for a
            hand just played reads as a bug rather than as loading, so say what is happening. */}
        {!teams ? (
          <Panel testId="scoring">
            <div className="row" style={{ justifyContent: 'center', gap: 10 }}>
              <span className="spinner" aria-hidden />
              <span className="muted">Scoring the hand…</span>
            </div>
          </Panel>
        ) : (
          <div className="handend" data-testid="scoreboard">
            {([0, 1] as const).map((t) => {
              const team = teams[t];
              return (
                <section
                  key={t}
                  className={`handend__col${mine === t ? ' handend__col--mine' : ''}`}
                  style={{ '--team': teamColorVar(t) } as React.CSSProperties}
                >
                  <header className="handend__head">
                    <span className="handend__team">{teamLabel(t, seat)}</span>
                    {mine === t && <span className="handend__badge">US</span>}
                  </header>

                  <div className="handend__score">
                    <span className="handend__total tnum" data-testid={`total-${t}`}>
                      {shown[t]}
                    </span>
                    <span className={`handend__delta tnum${gained === t ? ' handend__delta--won' : ''}`}>
                      <Delta n={team.delta} />
                    </span>
                  </div>

                  {(!team.made || team.boston) && (
                    <div className={`handend__stamp stamp${team.boston ? ' handend__stamp--boston' : ''}`}>
                      {/* Uppercase in the DOM, not only in CSS: `pip.spec.ts` reads this panel's
                          text content, which `text-transform` never reaches. */}
                      {team.boston ? 'BOSTON!' : 'SET'}
                    </div>
                  )}

                  <div className="handend__lines">
                    {handRows(team).map((row) => (
                      <Line key={row.label} {...row} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
